import LoanApplicationModal from "../../shared/LoanApplicationModal";
import { Eye, HelpCircle, Plus, Trash } from "lucide-react";
import { useQuery, useMutation } from "react-query";
import {
  deleteGuarantor,
  getApplicationGuarantors,
} from "../../sdk/applications/applications";
import { useLoanApplication } from "../../context/LoanApplicationContext";
import { useState } from "react";
import { useError } from "../../context/ErrorProvider";
import { useFormatAmount } from "../../hooks/useFormatAmount";

const Guarantors = ({
  closeGuarantorInformation,
  activeIndex,
  handleNextStep,
  handleAddGuarantor,
  handleAddGuarantorChattel,
  loading,
}) => {
  const { loanApplication } = useLoanApplication();
  const [expandedGuarantorId, setExpandedGuarantorId] = useState(null);
  const { showError } = useError();
  const formatAmount = useFormatAmount();

  const { data: guarantors, refetch: refetchGuarantors } = useQuery({
    queryKey: ["guarantors", loanApplication?.id],
    queryFn: () => getApplicationGuarantors(loanApplication?.id),
    initialData: [],
    enabled: !!loanApplication?.id,
  });

  const { mutate: deleteMyGuarantor } = useMutation({
    mutationKey: ["delete guarantor"],
    mutationFn: (id) => deleteGuarantor(id),
    onSuccess: () => {
      refetchGuarantors();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleDeleteGuarantor = (guarantor) => {
    deleteMyGuarantor(guarantor?.id);
  };

  const toggleExpand = (id) => {
    setExpandedGuarantorId((prev) => (prev === id ? null : id));
  };

  return (
    <LoanApplicationModal
      closeBioInformation={closeGuarantorInformation}
      activeIndex={activeIndex}
      handleNextStep={handleNextStep}
      loading={loading}
    >
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[#000000] font-normal text-[14px]">
            Create a New Loan Application
          </p>
          <p className="text-[#000000] font-semibold text-[20px]">
            Add Guarantor(s)
          </p>
          <p className="text-[#5C5C5C] text-[14px] font-normal">
            Add at least one guarantor for this loan
          </p>
        </div>
        <button
          onClick={handleAddGuarantor}
          className="h-[35px] bg-[#000000] rounded-[30px] px-[15px] text-[15px] font-bold text-white"
        >
          Add a Guarantor
        </button>
      </div>

      {/* Table Header */}
      <div className="h-[40px] w-full bg-[#F3F3F3] mt-[20px] px-[10px] flex items-center">
        <div className="w-[15%] text-[#222222] font-normal text-[14px]">ID</div>
        <div className="w-[25%] text-[#222222] font-normal text-[14px]">
          GUARANTOR NAME
        </div>
        <div className="w-[20%] text-[#222222] font-normal text-[14px]">
          LOCATION
        </div>
        <div className="w-[15%] text-[#222222] font-normal text-[14px]">
          CHATTELS
        </div>
        <div className="w-[25%] truncate text-[#222222] font-normal text-[14px]">
          VIEW/ DOWNLOAD/ EDIT
        </div>
      </div>

      {/* Guarantors Rows */}
      {guarantors?.length === 0 ? (
        <div className="h-[350px] flex flex-col items-center justify-center gap-[10px]">
          <HelpCircle />
          <p>No guarantor information available</p>
        </div>
      ) : (
        guarantors?.map((guarantor) => (
          <div key={guarantor.id}>
            {/* Row */}
            <div className="h-[40px] w-full bg-white px-[10px] flex items-center">
              <div className="w-[15%] text-[#000000] text-[14px]">
                {guarantor.public_id}
              </div>
              <div className="w-[25%] text-[#222222] text-[14px]">
                {guarantor.guarantorName}
              </div>
              <div className="w-[20%] text-[#222222] text-[14px]">
                {guarantor.residenceLocation}
              </div>
              <div className="w-[15%] text-[#222222] text-[14px]">
                {guarantor?.chattels?.length}
              </div>
              <div className="w-[25%] truncate flex gap-3 items-center">
                <Eye
                  className="w-4 h-4 text-[#222222] cursor-pointer"
                  onClick={() => toggleExpand(guarantor.id)}
                />
                <Trash
                  onClick={() => handleDeleteGuarantor(guarantor)}
                  fill="#222222"
                  className="w-4 h-4 text-[#222222] cursor-pointer"
                />
                <p
                  onClick={() => handleAddGuarantorChattel(guarantor?.id)}
                  className="text-[13px] flex items-center justify-center gap-[5px] bg-white border border-[#000000] rounded-[20px] px-[10px] py-[3px] text-[#000000] cursor-pointer"
                >
                  <Plus size={15} color="#000000" />
                  Add chattel
                </p>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedGuarantorId === guarantor.id && (
              <div className="bg-[#F9F9F9] px-4 py-3 text-sm text-[#333]">
                <p className="font-bold">Guarantor details</p>
                <p>
                  <span className="font-semibold">Phone:</span>{" "}
                  {guarantor.guarantorPhone || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Email:</span>{" "}
                  {guarantor.guarantorEmail || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Guarantee Amount:</span>{" "}
                  {formatAmount(guarantor.guaranteeAmount ?? 0) || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Residence:</span>{" "}
                  {guarantor.residenceLocation ?? "N/A"}
                </p>

                {/* Chattels Section (example) */}
                <div className="mt-3">
                  <p className="font-semibold text-[#000000]">Chattels</p>
                  {guarantor.chattels?.length > 0 ? (
                    <ul className="list-disc ml-6">
                      {guarantor.chattels.map((chattel) => (
                        <li key={chattel.id}>{chattel.description}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">No chattels added yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </LoanApplicationModal>
  );
};

export default Guarantors;
